<template>
  <Confirmacao v-if="modalExcluir" texto="Tem certeza que deseja excluir esta notícia?"
    @confirmacao="tratarConfirmacaoExclusao" />

  <Aviso v-if="mostrarAviso" :sucesso="avisoSucesso" :texto="textoAviso" @fecharModal="fecharAviso" />

  <div class="page-wrapper">
    <Breadcrumb :items="breadcrumbItems" />

    <carregamento v-if="carregando" />

    <main class="main-content" v-else-if="noticia">
      <div :class="{ 'noticia-layout': props.isAcceptance }">
        <div class="content-card" :class="{ 'noticia-main': props.isAcceptance }">
          <div class="header-container">
            <h1 class="page-title">{{ noticia.titulo }}</h1>

            <div class="action-buttons-top" v-if="podeEditar">
              <button type="button" class="btn btn-excluir" @click="excluirNoticia">
                <i class="ti ti-trash"></i>
                Excluir
              </button>
              <button type="button" @click="irParaEditar" class="btn btn-editar">
                <i class="ti ti-edit"></i>
                Editar
              </button>
            </div>
          </div>

          <div class="info-metadata">
            <div class="info-row">
              <div class="info-item">
                <span class="info-label">Data de publicação:</span>
                <span class="info-date">{{
                  formatarData(noticia.dataPublicacao)
                }}</span>
              </div>
              <div class="info-item" v-if="
                noticia.dataAtualizacao &&
                formatarData(noticia.dataAtualizacao) !==
                formatarData(noticia.dataPublicacao)
              ">
                <span class="info-label">Atualização:</span>
                <span class="info-date highlight">{{
                  formatarData(noticia.dataAtualizacao)
                }}</span>
              </div>
              <div class="info-item" v-if="noticia.fonte">
                <span class="info-label">Fonte:</span>
                <span class="info-value">{{ noticia.fonte }}</span>
              </div>
              <div class="info-item" v-if="noticia.palavrasChaves && noticia.palavrasChaves.length > 0">
                <span class="info-label">Palavras-chave:</span>
                <span v-for="palavrasChave in noticia.palavrasChaves" :key="palavrasChave" class="tag">
                  {{ palavrasChave }}
                </span>
              </div>
            </div>

            <div class="info-row" v-if="nomeAcaoExtensao">
              <div class="info-item">
                <span class="info-label">Ação de extensão:</span>
                <router-link
                  v-if="acaoExtensaoId"
                  :to="{ name: 'acao-informacoes', params: { id: acaoExtensaoId } }"
                  class="info-link"
                >
                  {{ nomeAcaoExtensao }}
                </router-link>
                <span v-else class="info-value">{{ nomeAcaoExtensao }}</span>
              </div>
            </div>
          </div>

          <hr class="divider" />

          <div class="imagem-destaque-wrapper" v-if="noticia.imagemUrl">
            <img :src="noticia.imagemUrl" :alt="noticia.descricaoImagem" class="imagem-destaque" />
          </div>

          <div class="noticia-texto-wrapper">
            <div v-if="noticia.texto" class="texto-noticia" v-html="noticia.texto"></div>
          </div>

          <div v-if="noticia.editada" class="modificada-info">
            Modificada em: {{ formatarData(noticia.dataEdicao) }}
          </div>
        </div>

        <aside v-if="props.isAcceptance" class="acceptance-sidebar">
          <h3>Validar Notícia</h3>
          <p>
            Revise o conteúdo da notícia antes de autorizar a publicação no
            portal.
          </p>

          <div class="sidebar-buttons">
            <button class="btn-accept" @click="aceitarNoticia" :disabled="processando">
              {{ processando ? "Processando..." : "Aceitar Notícia" }}
            </button>
            <button class="btn-reject" @click="rejeitarNoticia" :disabled="processando">
              {{ processando ? "Processando..." : "Rejeitar Notícia" }}
            </button>
            <button class="btn-back" @click="router.back()">
              Voltar
            </button>
          </div>
        </aside>
      </div>
    </main>

    <div v-else class="erro-container">
      <div class="content-card">
        <p>Notícia não encontrada.</p>
      </div>
    </div>
  </div>

  <transition name="toast">
    <div v-if="toastMsg" class="toast-notification">
      {{ toastMsg }}
    </div>
  </transition>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { formatarData } from "@/utils/FormatarEntradas";
import noticiaService from "@/service/noticiaService";
import router from "@/router";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import acaoService from "@/service/acaoService";
import userInfos from "@/utils/userInfos";
import Carregamento from "@/components/common/Carregamento.vue";
import Breadcrumb from "@/components/common/Breadcrumb.vue";
import Aviso from "@/components/common/aviso.vue";
import Confirmacao from "@/components/common/modalDeConfirmacao.vue";

// Controle do componente de aviso
const mostrarAviso = ref(false);
const avisoSucesso = ref(false);
const textoAviso = ref("");
const voltarAposAviso = ref(false);

const emit = defineEmits(["titulo", "fechar-noticia"]);
const noticia = ref(null);
const route = useRoute();
const btnCopy = ref("Copiar link");
const carregando = ref(true);
const processando = ref(false);
const dadosAcao = ref({});
const acaoIdResolvido = ref(null);
const estouNaAcao = ref(false);
const souCoordenador = ref(false);
const toastMsg = ref("");
const props = defineProps({
  noticiaId: String,
  id: String,
  acaoId: String,
  isAcceptance: Boolean,
});

function extrairNomeAcao(dadosNoticia, acaoCarregada) {
  if (!dadosNoticia) return "";
  const acao = dadosNoticia.acao;
  const acaoExt = dadosNoticia.acaoExtensao;

  const bruto =
    (typeof acao === "string" ? acao : acao?.titulo || acao?.nome) ||
    (typeof acaoExt === "string" ? acaoExt : acaoExt?.titulo || acaoExt?.nome) ||
    dadosNoticia.acaoTitulo ||
    dadosNoticia.acaoNome ||
    dadosNoticia.nomeAcao ||
    dadosNoticia.acaoExtensaoTitulo ||
    dadosNoticia.acaoExtensaoNome ||
    acaoCarregada?.titulo ||
    acaoCarregada?.nome ||
    "";

  return String(bruto).replace(/^"+|"+$/g, "").trim();
}

function extrairAcaoIdDireto(dadosNoticia) {
  const acaoUrlPartes = dadosNoticia?.acaoUrl?.split("/").filter(Boolean) || [];
  const idViaUrl = acaoUrlPartes[acaoUrlPartes.length - 1];

  return (
    dadosNoticia?.acaoId ||
    dadosNoticia?.acaoExtensaoId ||
    (typeof dadosNoticia?.acao === "object" ? dadosNoticia?.acao?.id : null) ||
    (typeof dadosNoticia?.acaoExtensao === "object"
      ? dadosNoticia?.acaoExtensao?.id
      : null) ||
    idViaUrl ||
    route.params.id ||
    props.acaoId ||
    props.id ||
    null
  );
}

const nomeAcaoExtensao = computed(() =>
  extrairNomeAcao(noticia.value, dadosAcao.value)
);

const acaoExtensaoId = computed(
  () => extrairAcaoIdDireto(noticia.value) || acaoIdResolvido.value
);

async function resolverAcaoVinculada(dadosNoticia) {
  if (extrairAcaoIdDireto(dadosNoticia)) return;

  const nome = extrairNomeAcao(dadosNoticia, dadosAcao.value);
  if (!nome) return;

  try {
    const resultado = await acaoService.getAcoesFiltro(0, 15, null, null, nome);
    const lista = Array.isArray(resultado)
      ? resultado
      : resultado?.content || [];
    const normalizar = (valor) =>
      String(valor || "")
        .replace(/"/g, "")
        .trim()
        .toLowerCase();

    const alvoTitulo = normalizar(nome);
    const alvoSigla = normalizar(dadosNoticia?.acaoSigla);
    const candidatos = lista.filter(
      (item) => normalizar(item.titulo || item.nome) === alvoTitulo
    );
    const encontrada =
      (alvoSigla &&
        candidatos.find((item) => normalizar(item.sigla) === alvoSigla)) ||
      candidatos[0];

    if (encontrada?.id) {
      acaoIdResolvido.value = encontrada.id;
    }
  } catch (error) {
    console.error("Erro ao resolver ação vinculada da notícia:", error);
  }
}

const podeEditar = computed(() => {
  const auth = useAuthStore();
  return (
    auth.hasRole("ADMINISTRADOR") ||
    auth.hasRole("PREX") ||
    souCoordenador.value
  );
});

const breadcrumbItems = computed(() => {
  const acaoId = route.params.id;
  if (acaoId) {
    return [
      { label: "Início", to: "/" },
      { label: "Ações", to: "/suas-acoes" },
      { tipo: "acao", id: acaoId, to: `/acao/${acaoId}` },
      { label: "Notícias", to: `/acao/${acaoId}/noticias-acao` },
      {
        label: noticia.value?.titulo || "Notícia",
        to: `/acao/${acaoId}/noticias/${props.noticiaId}`,
      },
    ];
  } else {
    return [
      { label: "Início", to: "/" },
      { label: "Noticias", to: "/noticias" },
      {
        label: noticia.value?.titulo || "Notícia",
        to: `/noticias/${props.noticiaId}`,
      },
    ];
  }
});

function mudarRota() {
  router.back();
}

const modalExcluir = ref(false);

function excluirNoticia() {
  if (!noticia.value) return;

  modalExcluir.value = true;
}

async function tratarConfirmacaoExclusao(confirmado) {
  modalExcluir.value = false;

  // O usuário clicou em "Não" ou fechou o modal.
  if (!confirmado) return;

  try {
    await noticiaService.excluirNoticia(noticia.value.id);

    avisoSucesso.value = true;
    textoAviso.value = "Notícia excluída com sucesso!";
    voltarAposAviso.value = true;
    mostrarAviso.value = true;
  } catch (error) {
    console.error("Erro ao excluir notícia:", error);

    avisoSucesso.value = false;
    textoAviso.value =
      "Não foi possível excluir a notícia. Tente novamente.";
    voltarAposAviso.value = false;
    mostrarAviso.value = true;
  }
}
// Fechar aviso
function fecharAviso() {
  mostrarAviso.value = false;

  if (voltarAposAviso.value) {
    voltarAposAviso.value = false;
    router.back();
  }
}
async function carregarNoticia(id) {
  if (!id) return;
  try {
    carregando.value = true;
    noticia.value = null;
    acaoIdResolvido.value = null;
    const response = await noticiaService.getNoticiaById(id);
    if (response) {
      noticia.value = response;
      await resolverAcaoVinculada(response);
      await verificarPermissoes();
    }
  } catch (error) {
    console.error("Erro ao carregar notícia:", error);
    noticia.value = null;
  } finally {
    carregando.value = false;
  }
}

function irParaEditar() {
  if (!noticia.value) return;
  const targetAcaoId =
    route.params.id ||
    props.id ||
    props.acaoId ||
    noticia.value.acaoId ||
    noticia.value.acao?.id ||
    0;

  router.push({
    name: "editar-noticia",
    params: {
      id: targetAcaoId,
      noticiaId: noticia.value.id,
    },
  });
}
async function aceitarNoticia() {
  if (!noticia.value) return;
  try {
    processando.value = true;
    await noticiaService.autorizarNoticia(noticia.value.id, true);
    showToast("Notícia autorizada com sucesso!");
    setTimeout(() => router.back(), 1500);
  } catch (error) {
    console.error("Erro ao aceitar notícia:", error);
  } finally {
    processando.value = false;
  }
}

async function rejeitarNoticia() {
  if (!noticia.value) return;
  // const motivo = prompt("Por favor, informe o motivo da rejeição:");
  // if (motivo === null) return; // Usuário cancelou o prompt
  const motivo =
    "Notícia rejeitada pelo administrador. Entre em contato para mais detalhes.";

  try {
    processando.value = true;
    // Agora usando a mesma rota de autorização, mas com autorizado: false
    await noticiaService.autorizarNoticia(noticia.value.id, false, motivo);
    showToast("Notícia rejeitada com sucesso!");
    setTimeout(() => router.back(), 1500);
  } catch (error) {
    console.error("Erro ao rejeitar notícia:", error);
  } finally {
    processando.value = false;
  }
}

function showToast(msg) {
  toastMsg.value = msg;
  setTimeout(() => (toastMsg.value = ""), 2500);
}

function shareInstagram() {
  const url = window.location.origin + route.fullPath;
  navigator.clipboard
    .writeText(url)
    .then(() => showToast("Link copiado! Cole no Instagram Stories ou na bio."))
    .catch(() => showToast("Não foi possível copiar. Tente manualmente."));
}

function shareTwitter() {
  const text = encodeURIComponent(
    "Confira esta notícia do GEx — Pró-Reitoria de Extensão da UFC!"
  );
  const url = encodeURIComponent(window.location.origin + route.fullPath);
  window.open(
    `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
    "_blank",
    "noopener,noreferrer"
  );
}

function copyLink() {
  const fullUrl = window.location.origin + route.fullPath;
  navigator.clipboard
    .writeText(fullUrl)
    .then(() => {
      btnCopy.value = "Copiado";
      setTimeout(() => {
        btnCopy.value = "Copiar link";
      }, 2000);
    })
    .catch(() => alert("Não foi possível copiar. Tente novamente!"));
}

watch(
  () => props.noticiaId || route.params.noticiaId,
  async (id) => {
    if (id) await carregarNoticia(id);
  },
  { immediate: true }
);

async function verificarPermissoes() {
  const auth = useAuthStore();
  if (auth.hasRole("ADMINISTRADOR")) {
    souCoordenador.value = true;
    return;
  }

  const targetAcaoId =
    route.params.id ||
    props.id ||
    props.acaoId ||
    noticia.value?.acaoId ||
    noticia.value?.acao?.id;

  if (targetAcaoId) {
    try {
      souCoordenador.value =
        await acaoService.verificarCoordenador(targetAcaoId);
    } catch (error) {
      console.error("Erro ao verificar coordenação:", error);
      souCoordenador.value = false;
    }
  } else {
    souCoordenador.value = false;
  }
}

onMounted(async () => {
  carregando.value = true;
  if (route.params.id) {
    try {
      dadosAcao.value = await acaoService.getAcoesById(route.params.id);
      estouNaAcao.value = true;
    } catch (e) {
      console.error("Erro ao carregar dados da ação:", e);
    }
  }
  await verificarPermissoes();
  carregando.value = false;
});
</script>

<style scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  width: 100%;
  margin: 0;
  padding: 0;
}

.content-card {
  background: var(--branco);

  min-height: 50vh;
}

.noticia-layout {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

.noticia-main {
  flex: 1;
  width: 100%;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--preto);
  margin: 0;
  line-height: 1.3;
}

.action-buttons-top {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  border: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn:active {
  transform: scale(0.97);
}

.btn-excluir {
  background: var(--branco);
  border: 1.5px solid var(--vermelho);
  color: var(--vermelho);
}

.btn-excluir:hover {
  background: var(--vermelho);
  color: var(--branco);
}

.btn-editar {
  background: var(--roxo-primario);
  color: var(--branco);
}

.btn-editar:hover {
  background: var(--roxo-medio);
  color: white;
}

.info-metadata {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.info-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--cinza-escuro);
}

.info-date {
  font-size: 13px;
  border: 1px solid var(--cinza-claro);
  border-radius: 20px;
  padding: 2px 10px;
  color: var(--cinza-escuro);
  background: var(--branco);
}

.info-date.highlight {
  border-color: var(--roxo-primario);
  color: var(--branco);
  background: var(--roxo-medio);
  font-weight: 600;
}

.info-value {
  font-size: 13px;
  color: var(--cinza-escuro);
}

.info-link {
  font-size: 13px;
  font-weight: 500;
  color: var(--roxo-medio);
  text-decoration: none;
  transition: color 0.2s ease;
}

.info-link:hover {
  color: var(--roxo-primario);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.info-link:focus-visible {
  outline: 2px solid var(--roxo-claro);
  outline-offset: 2px;
  border-radius: 2px;
}

.divider {
  border: 0;
  border-top: 1px solid var(--cinza-claro);
  margin: 0 0 24px 0;
}

.imagem-destaque-wrapper {
  margin-bottom: 30px;
  text-align: center;
  width: 100%;
}

.imagem-destaque {
  max-width: 100%;
  max-height: 450px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px hsla(0, 0%, 0%, 0.1);
}

.img-legenda {
  font-size: 13px;
  color: var(--cinza-escuro);
  margin: 10px 0 0 0;
  font-style: italic;
}

.noticia-texto-wrapper {
  color: var(--cinza-escuro);
  font-size: 16px;
  line-height: 1.8;
  text-align: left;
}

.texto-noticia {
  font-family: "Inter", "Roboto", "Arial", sans-serif;
}

.texto-noticia p {
  margin-bottom: 1.2rem;
}

.texto-noticia img {
  max-width: 100%;
  border-radius: 6px;
}

.modificada-info {
  margin-top: 30px;
  padding-top: 12px;
  border-top: 1px dashed var(--cinza-claro);
  font-size: 12.5px;
  color: var(--cinza-medio);
  font-style: italic;
}

.tag-icon {
  color: var(--roxo-primario);
  flex-shrink: 0;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  border: 1px solid var(--roxo-claro);
  border-radius: 16px;
  padding: 4px 14px;
  color: var(--roxo-primario);
  background: var(--roxo-superficie);
  font-weight: 500;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.tag:hover {
  background: var(--roxo-superficie);
  border-color: var(--roxo-primario);
}

.acceptance-sidebar {
  width: 320px;
  background-color: var(--branco);
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow:
    0 4px 6px -1px hsla(0, 0%, 0%, 0.1),
    0 2px 4px -1px hsla(0, 0%, 0%, 0.06);
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.acceptance-sidebar h3 {
  margin: 0;
  color: var(--roxo-primario);
  font-size: 18px;
  font-weight: 700;
}

.acceptance-sidebar p {
  font-size: 14px;
  color: var(--cinza-escuro);
  line-height: 1.5;
  margin: 0;
}

.sidebar-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.btn-accept {
  padding: 10px;
  background-color: var(--verde);
  color: var(--branco);
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-accept:hover {
  background-color: var(--verde);
}

.btn-reject {
  padding: 10px;
  background-color: var(--vermelho);
  color: var(--branco);
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-reject:hover {
  background-color: var(--vermelho);
}

.btn-back {
  padding: 10px;
  background-color: transparent;
  color: var(--cinza-escuro);
  border: 1px solid var(--cinza-claro);
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-back:hover {
  background-color: var(--cinza-claro);
}

.btn-accept:disabled,
.btn-reject:disabled {
  background-color: var(--cinza-medio);
  cursor: not-allowed;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 1rem;
  color: var(--cinza-medio);
}

.spinner {
  width: 38px;
  height: 38px;
  border: 4px solid var(--cinza-claro);
  border-top-color: var(--roxo-medio);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.erro-container {
  text-align: center;
  padding: 3rem 2rem;
  color: var(--cinza-escuro);
}

.erro-container p {
  font-size: 16px;
  margin: 0;
}


.modal-box {
  background: transparent;
  border-radius: 8px;
  width: 340px;
  overflow: hidden;

}




.modal-body {
  padding: 1rem;
  background: var(--branco);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0px;
}

.modal-icone {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--roxo-primario);
  color: var(--branco);
  font-size: 24px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-texto {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0rem;
  margin-top: 0.5rem;
  color: var(--preto);
  text-align: center;
  line-height: 1.5;
}



.modal-botoes {
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
  margin-top: 1rem;
}

.btn-modal-cancelar {
  padding: 8px 24px;
  border-radius: 6px;
  background: var(--roxo-primario);
  color: var(--branco);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-modal-cancelar:hover {
  background: var(--roxo-medio);
}

.btn-modal-confirmar {
  padding: 8px 24px;
  border-radius: 6px;
  border: none;
  background: var(--vermelho-escuro);
  color: var(--branco);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-modal-confirmar:hover {
  background: var(--vermelho);
}

.toast-notification {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--roxo-primario);
  color: var(--branco);
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow:
    0 10px 15px -3px hsla(0, 0%, 0%, 0.1),
    0 4px 6px -2px hsla(0, 0%, 0%, 0.05);
  z-index: 2000;
  font-weight: 600;
  font-size: 14px;
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(16px);
}

@media (max-width: 768px) {
  .noticia-layout {
    flex-direction: column;
  }

  .content-card {
    padding: 24px 20px;
  }

  .header-container {
    flex-direction: column;
    gap: 12px;
  }

  .action-buttons-top {
    width: 100%;
    justify-content: flex-start;
  }

  .acceptance-sidebar {
    width: 100%;
    position: static;
  }
}
</style>
